/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import {
	createActionsHook,
	createHook,
	createStateHook,
	type Action,
	type BoundActions,
	type HookActionsFunction,
	type HookFunction,
	type HookStateFunction,
} from '@atlassian/react-sweet-state';
import type {
	BannerState,
	BottomRightPortalState,
	LayoutState,
	LeftSidebarState,
	PreviewPanelState,
	RightSidebarState,
} from '../../../../common/types.tsx';
import type { LayoutActions } from '../../actions/index.tsx';
import { layoutStore } from '../../store/index.tsx';

/**
 * Hook to get the current fullscreen state value only.
 * Use this when you only need to read isFullscreen and do not need actions.
 */
export const useIsFullscreenState: HookStateFunction<boolean, undefined> = createStateHook<
	LayoutState,
	LayoutActions,
	boolean,
	undefined
>(layoutStore, {
	selector: (state) => state.isFullscreen,
});

/**
 * Hook to get fullscreen actions only (e.g. setIsFullscreen).
 * Use this when you only need to call actions and do not need to subscribe to state changes.
 */
export const useFullscreenActions: HookActionsFunction<
	BoundActions<
		LayoutState,
		{
			readonly setIsChromeless: (isChromeless: boolean) => Action<LayoutState>;
			readonly setIsFullscreen: (isFullscreen: boolean) => Action<LayoutState>;
			readonly showBanner: (newState: BannerState) => Action<LayoutState>;
			readonly dismissCurrentBanner: () => Action<LayoutState>;
			readonly setLeftSidebarState: (newState: Partial<LeftSidebarState>) => Action<LayoutState>;
			readonly setRightSidebarState: (newState: RightSidebarState) => Action<LayoutState>;
			readonly openRightSidebar: (
				panelId: string,
				width: number,
				isMinimised?: boolean,
				toTop?: boolean,
			) => Action<LayoutState>;
			readonly closeRightSidebar: (panelId: string) => Action<LayoutState>;
			readonly closeAllRightSidebar: () => Action<LayoutState>;
			readonly closeCurrentRightSidebar: () => Action<LayoutState>;
			readonly minimiseRightSidebar: (panelId: string) => Action<LayoutState>;
			readonly registerBottomRightCornerContainer: (
				ref: HTMLElement | null | undefined,
			) => Action<LayoutState>;
			readonly insertBottomRightCornerPortal: (
				item: BottomRightPortalState,
			) => Action<LayoutState, void, number>;
			readonly removeBottomRightCornerPortal: (id: string) => Action<LayoutState>;
			readonly setPreviewPanelState: (newState: Partial<PreviewPanelState>) => Action<LayoutState>;
		}
	>
> = createActionsHook<LayoutState, LayoutActions>(layoutStore);

/**
 * @deprecated Use `useIsFullscreenState` for state-only access or `useFullscreenActions` for
 * action-only access. This combined hook causes unnecessary re-renders in action-only consumers.
 */
export const useIsFullscreen: HookFunction<
	boolean,
	BoundActions<
		LayoutState,
		{
			readonly setIsChromeless: (isChromeless: boolean) => Action<LayoutState>;
			readonly setIsFullscreen: (isFullscreen: boolean) => Action<LayoutState>;
			readonly showBanner: (newState: BannerState) => Action<LayoutState>;
			readonly dismissCurrentBanner: () => Action<LayoutState>;
			readonly setLeftSidebarState: (newState: Partial<LeftSidebarState>) => Action<LayoutState>;
			readonly setRightSidebarState: (newState: RightSidebarState) => Action<LayoutState>;
			readonly openRightSidebar: (
				panelId: string,
				width: number,
				isMinimised?: boolean,
				toTop?: boolean,
			) => Action<LayoutState>;
			readonly closeRightSidebar: (panelId: string) => Action<LayoutState>;
			readonly closeAllRightSidebar: () => Action<LayoutState>;
			readonly closeCurrentRightSidebar: () => Action<LayoutState>;
			readonly minimiseRightSidebar: (panelId: string) => Action<LayoutState>;
			readonly registerBottomRightCornerContainer: (
				ref: HTMLElement | null | undefined,
			) => Action<LayoutState>;
			readonly insertBottomRightCornerPortal: (
				item: BottomRightPortalState,
			) => Action<LayoutState, void, number>;
			readonly removeBottomRightCornerPortal: (id: string) => Action<LayoutState>;
			readonly setPreviewPanelState: (newState: Partial<PreviewPanelState>) => Action<LayoutState>;
		}
	>,
	void
> = createHook(layoutStore, {
	selector: (state) => state.isFullscreen,
});
